<template>
  <contents-box
    type="inner"
    :class="$style.reserve_box">
    <ul>
      <li>
        <label-box label="担当" color="gray" size="sm" :class="$style.reserve_box_label" />
        <p :class="$style.content">
          {{ reserve.toUser.username }}（{{ reserve.toUser.account_type === 21 ? '医師' : '心理士' }}）
          <i
            v-if="isOnline"
            class="fa-regular fa-laptop"
            :class="[
              $style.counseling_icon,
              $style.online,
            ]"
          ><i :class="$style.counseling_icon_inner" class="fa-regular fa-user-headset"/></i>
          <i
            v-if="isVirtual"
            class="fa-solid fa-vr-cardboard"
            :class="[
              $style.counseling_icon,
              $style.virtual,
            ]"
          ></i>
        </p>
      </li>
      <li>
        <label-box label="実施日" color="gray" size="sm" :class="$style.reserve_box_label" />
        <p :class="$style.content">{{ formatTimestamp(reserve.schedule.date, 'YYYY/MM/DD HH:00〜HH:45') }}</p>
      </li>
      <li>
        <label-box label="お申込日時" color="gray" size="sm" :class="$style.reserve_box_label" />
        <p :class="$style.content">{{ formatTimestamp(reserve.created_at, 'YYYY/MM/DD HH:mm') }}</p>
      </li>
      <li>
        <label-box label="お悩み" color="gray" size="sm" :class="$style.reserve_box_label" />
        <div :class="[$style.value, $style.type, hasSubTypesRecorded(reserve) ? $style.typeCompany : '']">
          <p
            class="n2br"
            v-for="item in types"
            :key="item">{{ getWorryTypeLabel(item, isCompanyEffective, 'reserve') }}<span v-if="item === 6 && (reserve.other_text || reserve.type.text)">（{{ reserve.other_text || reserve.type.text }}）</span>
            <span v-if="getWorrySelectedSubText(item, getRecordedSubs(reserve, item), isCorporateEffective, 'reserve')" :class="$style.subList">{{ getWorrySelectedSubText(item, getRecordedSubs(reserve, item), isCorporateEffective, 'reserve') }}</span>
          </p>
        </div>
      </li>
      <li v-if="reserve.memos.customer.length && reserve.memos.customer[0].memo">
        <label-box label="相談内容" color="gray" size="sm" :class="$style.reserve_box_label" />
        <p :class="$style.content" class="multilineText">{{ reserve.memos.customer[0].memo }}</p>
      </li>
    </ul>
  </contents-box>
</template>

<script>
import { mapState } from 'vuex';
import ContentsBox from '@/views/components/ContentsBox.vue';
import LabelBox from '@/views/components/LabelBox.vue';

import cf from '@/mixins/commonFunctions.js';
import corporateSchool from '@/mixins/corporateSchool.js';

export default {
  name: 'reserve-info-box',
  mixins: [cf, corporateSchool],
  components: {
    ContentsBox,
    LabelBox,
  },
  props: {
    reserve: {
      type: Object,
      required: true,
    },
    // 顧客の所属校が企業アカウントか。reserve.fromUser.school が eager-load されない文脈
    //（Histories の相談予約 preview / Base の予約リスト = customer.reserves 由来）で親から渡す。
    // 未指定の文脈（Operator/UserList = /v2/reserve/get/list 由来）は reserve.fromUser.school から判定。
    isCompany: {
      type: Boolean,
      default: false,
    },
    // 顧客の所属校が企業（is_company かつ is_corporate）か。isCompany と同様に
    // reserve.fromUser.school が eager-load されない文脈で親から渡す。
    // 「未指定」と「明示 false」を区別する必要があるため既定は null（false を既定にすると
    // 未指定文脈でフォールバックが効かず、サブが常に非表示になる）。
    isCorporate: {
      type: Boolean,
      default: null,
    },
  },
  computed: {
    ...mapState(['helper']),
    counselingType() {
      if (this.reserve.counseling_type) {
        return this.reserve.counseling_type;
      }
      if (this.reserve.type && this.reserve.type.counseling_type) {
        return this.reserve.type.counseling_type;
      }
      return 0; // デフォルトは0（未定義）
    },
    isOnline() {
      return this.counselingType === 1;
    },
    isVirtual() {
      return this.counselingType === 3;
    },
    types() {
      // this.reserve.typeが配列の場合とオブジェクトの場合を考慮
      // 大項目はチェック順で保存されるため、表示順をマスタ番号昇順へ正す
      if (Array.isArray(this.reserve.type)) {
        return this.sortWorryTypes(this.reserve.type);
      }
      if (this.reserve.type && this.reserve.type.type) {
        return this.sortWorryTypes(this.reserve.type.type);
      }
      return [];
    },
    // お悩みラベルの企業/学校バリアント判定。親が渡した isCompany を優先し、
    // 未指定文脈では reserve 自身の fromUser.school から判定する。
    isCompanyEffective() {
      return this.isCompany || !!this.reserve?.fromUser?.school?.[0]?.is_company;
    },
    // 企業固有のお悩みサブ項目ゲート専用。ラベル(isCompanyEffective)と違い prop isCompany は
    // 引き継がず、企業（is_company かつ is_corporate）のときだけ true。インターンでは出さない。
    // 親が isCorporate を渡した文脈はそれを優先し、未指定文脈では reserve 自身の
    // fromUser.school から判定する（fromUser が eager-load されない Histories / Counseling Base では
    // 親から渡さないと常に非表示になる）。
    isCorporateEffective() {
      if (this.isCorporate !== null) return this.isCorporate;
      return this.isCorporateSchool(this.reserve?.fromUser?.school);
    },
  },
  data() {
    return {
    };
  },
};
</script>

<style lang="scss" module>
.reserve_box {
  width: 100%;
  margin-bottom: .5rem;
  li {
    display: flex;
    align-items: flex-start;
    .content {
      margin-top: 3px;
      flex: 1;
      word-break: break-all;
    }
    &:not(:last-child) {
      margin-bottom: 15px;
    }
  }
  @include sm-view {
    font-size: 14px;
    li {
      display: block;
      .label {
        width: 100%;
        margin: 0 0 .5em;
        text-align: left;
        padding: .5em;
      }
      &:not(:first-child) {
        margin-top: 1.4em;
      }
    }
  }
  .reserve_box_label {
    min-width: 6rem;
    max-width: 6rem;
    margin-right: 0.5rem;
  }
  .counseling {
    display: flex;
    align-items: center;
    &_wrap {
      display: flex;
      align-items: center;
    }
    &_icon {
      font-size: 25px !important;
      position: relative;
      &_inner {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -60%);
        font-size: 10px !important;
      }
    }
  }
  .counseling_icon {
    &.online {
      color: var(--orange-main);
    }
    &.virtual {
      color: var(--green-main);
    }
  }
  .value {
    flex: 1;
    margin-top: 3px;
    &.type {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      p {
        // お悩みが長い場合に折り返さない問題を修正
        // white-space: nowrap;
        &:not(:last-child) {
          &::after {
            margin: 0 5px;
            content: '/';
          }
        }
        // 「その他」自由記述などで長い半角英字連続が来た時の折り返し（#3244）
        > span {
          word-break: break-all;
          overflow-wrap: break-word;
          // 自由記述は本文 bold とは別の通常重みで表示する
          font-weight: normal;
        }
      }
    }
    // 企業ユーザー時のみ、メイン項目を縦並びにしてセパレータを消す
    &.typeCompany {
      display: block;
      p:not(:last-child)::after {
        content: '';
        margin: 0;
      }
    }
  }
}
// 企業サイト: メイン項目の下に「選択済み」サブ項目を ' / ' 連結で表示する。
// 表示形は予約入力の確認ステップ（ReserveFormWizard の確認欄）に合わせてある。
// span を block 化して親 <p> 内で改行させる。テンプレ側は1行で書くこと
// （親に white-space: pre-wrap が効く画面があり、改行・インデントが空白として可視化されるため）。
.subList {
  display: block;
  margin: 2px 0 8px 20px;
  font-size: 13px;
  color: #555;
}
</style>
